import { ReactNode, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2,
  ChevronDown,
  ExternalLink,
  Lock,
  Menu,
  Settings,
  Shield,
  User,
  X,
} from "lucide-react";
import { TouchTooltip } from "@/components/ui/touch-tooltip";
import { cn } from "@/lib/utils";
import type { SessionStatus } from "@/types/common";
import type { EntityData } from "@/types/entity";
import type { RiskAnalysisBySession } from "@/types/risk-analysis";
import type {
  NavigationData,
  NavigationPage,
  PageStatus,
} from "@/types/navigation";
import {
  getLockedTooltip,
  resolvePageStatus,
} from "@/utils/guideProgressCalculator";
import { useOnboardingModal } from "@/contexts/OnboardingModalContext";

export type MobileSidebarProps = {
  navigationData: NavigationData;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  beforeMenu?: ReactNode;
  sessionSwitcherSlot?: ReactNode;
  afterMenu?: ReactNode;
  className?: string;
  entityData?: EntityData | null;
  riskData?: RiskAnalysisBySession;
  sessionId?: string | null;
  sessionStatus?: SessionStatus | null;
  isAuthenticated?: boolean;
  userEmail?: string | null;
};

export type MobileSidebarTriggerProps = {
  onClick: () => void;
  className?: string;
  buttonClassName?: string;
};

function getStatusIndicator(status?: string) {
  switch (status) {
    case "locked":
      return <Lock size={12} className="text-ds-text-400" />;
    case "completed":
      return <CheckCircle2 size={12} className="text-ds-emerald-500" />;
    case "current":
    case "in-progress":
      return <div className="w-2 h-2 bg-ds-blue-600 rounded-full animate-pulse" />;
    case "pending":
      return <div className="w-1.5 h-1.5 bg-ds-border-300 rounded-full" />;
    default:
      return null;
  }
}

export function MobileSidebarTrigger({
  onClick,
  className,
  buttonClassName,
}: MobileSidebarTriggerProps) {
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onClick}
      className={cn("text-white hover:bg-white/20 p-2", buttonClassName)}
    >
      <Menu className="h-5 w-5" />
    </Button>
  );
}

export function MobileSidebar({
  navigationData,
  isOpen: controlledIsOpen,
  onOpenChange,
  beforeMenu,
  sessionSwitcherSlot,
  afterMenu,
  className,
  entityData,
  riskData,
  sessionId,
  sessionStatus,
  isAuthenticated = true,
  userEmail,
}: MobileSidebarProps) {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = controlledIsOpen ?? internalIsOpen;
  const setIsOpen = onOpenChange ?? setInternalIsOpen;

  const { showOnboardingModal } = useOnboardingModal();
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;

  const getPageUrl = (page: NavigationPage): string | null => {
    if (page.dynamicUrl) {
      return page.dynamicUrl(sessionId);
    }
    return page.url || null;
  };

  const isPageActive = (page: NavigationPage) => {
    const url = getPageUrl(page);
    if (!url) return false;
    return (
      currentPath === url ||
      (page.url === "/identificare-raportare" && currentPath === "/wizard")
    );
  };

  const shouldShowPage = (page: NavigationPage) => {
    if (page.requiresAuth && !isAuthenticated) return false;
    if (page.conditionalDisplay) {
      return page.conditionalDisplay(
        entityData,
        sessionId,
        sessionStatus ?? null
      );
    }
    return true;
  };

  const getResolvedStatus = (
    page: NavigationPage
  ): PageStatus | string | undefined => {
    return resolvePageStatus(page, entityData, riskData);
  };

  const handlePageClick = (page: NavigationPage) => {
    if (page.externalLink) {
      window.open(page.externalLink, "_blank", "noopener,noreferrer");
      return;
    }
    const url = getPageUrl(page);
    if (getResolvedStatus(page) === "locked" || !url) return;
    if (
      page.id === "notificare-entitate" ||
      page.id === "formular-notificare"
    ) {
      setIsOpen(false);
      showOnboardingModal();
      return;
    }
    navigate(url);
    setIsOpen(false);
  };

  const getUserDisplayName = () => {
    if (!userEmail) return "Utilizator";
    const name = userEmail.split("@")[0];
    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  // Get default expanded subsections
  const defaultExpanded = navigationData.subsections
    .filter((s) => s.defaultExpanded)
    .map((s) => s.id);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={() => setIsOpen(false)}
      />

      {/* Sidebar */}
      <div
        className={cn(
          "fixed top-0 left-0 h-full w-80 bg-white border-r border-ds-border-200 z-50 transform transition-transform duration-300 ease-in-out flex flex-col",
          className
        )}
      >
        {/* Header */}
        <div className="p-4 border-b border-ds-border-200">
          <div className="flex items-center justify-between">
            <Link
              to="/"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-3"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-ds-blue-600 to-ds-blue-700 rounded-xl flex items-center justify-center shadow-lg shadow-ds-blue-200">
                <Shield size={20} className="text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold font-heading text-navy-800">NIS2PASS</h1>
                <p className="text-xs text-ds-text-500">Platformă Conformitate</p>
              </div>
            </Link>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(false)}
              className="p-2"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3">
          {beforeMenu}

          {/* Session Switcher with Entity Card */}
          {sessionSwitcherSlot && (
            <div className="mb-3">{sessionSwitcherSlot}</div>
          )}

          {/* Navigation */}
          <Accordion
            type="multiple"
            defaultValue={defaultExpanded}
            className="space-y-1"
          >
            {navigationData.subsections.map((subsection) => {
              const SubsectionIcon = subsection.icon;
              const visiblePages = subsection.pages.filter(shouldShowPage);

              if (visiblePages.length === 0) return null;

              const hasActiveChild = visiblePages.some(isPageActive);

              return (
                <AccordionItem
                  key={subsection.id}
                  value={subsection.id}
                  className="border-none"
                >
                  <AccordionTrigger
                    className={cn(
                      "px-3 py-2.5 rounded-xl hover:no-underline transition-all [&[data-state=open]>div>.chevron]:rotate-180",
                      hasActiveChild
                        ? "bg-ds-blue-50 text-ds-blue-700"
                        : "text-ds-text-600 hover:bg-ds-surface-50"
                    )}
                  >
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-3">
                        <SubsectionIcon
                          size={18}
                          className={
                            hasActiveChild ? "text-ds-blue-600" : "text-ds-text-400"
                          }
                        />
                        <span className="font-medium text-sm">
                          {subsection.title}
                        </span>
                      </div>
                      <ChevronDown
                        size={16}
                        className={cn(
                          "chevron transition-transform duration-200",
                          hasActiveChild ? "text-ds-blue-600" : "text-ds-text-400"
                        )}
                      />
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-1 pb-0">
                    <div className="ml-4 pl-4 border-l-2 border-ds-border-200 space-y-0.5">
                      {visiblePages.map((page) => {
                        const PageIcon = page.icon;
                        const isActive = isPageActive(page);
                        const status = getResolvedStatus(page);
                        const isLocked = status === "locked";
                        const tooltipText = isLocked
                          ? getLockedTooltip(page.id)
                          : null;

                        const pageButton = (
                          <button
                            onClick={() => handlePageClick(page)}
                            disabled={isLocked}
                            className={cn(
                              "w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all text-left",
                              isActive
                                ? "bg-ds-blue-100 text-ds-blue-700 font-medium"
                                : isLocked
                                  ? "text-ds-text-400 cursor-not-allowed"
                                  : "text-ds-text-600 hover:bg-ds-surface-50"
                            )}
                          >
                            <div className="flex items-center gap-2">
                              <PageIcon size={14} />
                              <span>{page.shortTitle || page.title}</span>
                              {page.externalLink && (
                                <ExternalLink
                                  size={10}
                                  className="text-ds-text-400"
                                />
                              )}
                            </div>
                            {getStatusIndicator(status)}
                          </button>
                        );

                        return (
                          <div key={page.id}>
                            {tooltipText ? (
                              <TouchTooltip content={tooltipText}>
                                {pageButton}
                              </TouchTooltip>
                            ) : (
                              pageButton
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>

          {afterMenu}
        </div>

        {/* User Profile Footer */}
        <div className="p-3 border-t border-ds-border-200">
          <div className="flex items-center gap-3 px-3 py-2 rounded-xl bg-ds-surface-50">
            <div className="w-8 h-8 bg-ds-blue-100 rounded-full flex items-center justify-center">
              <User size={16} className="text-ds-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-ds-text-600 truncate">
                {getUserDisplayName()}
              </p>
              <p className="text-xs text-ds-text-500 truncate">
                {isAuthenticated ? "Utilizator" : "Neautentificat"}
              </p>
            </div>
            <button
              onClick={() => {
                navigate("/profil");
                setIsOpen(false);
              }}
              className="p-1.5 hover:bg-ds-border-200 rounded-lg transition-colors"
            >
              <Settings size={16} className="text-ds-text-400" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
